import { useEffect, useState } from "react";

import { useGetCommentFiles } from "../../../pages/dashboard/tabs/journal/api-hooks";
import { IComment } from "../../../pages/dashboard/tabs/journal/interfaces";
import { IMediaFile } from "../../common/media-renderer";
import { createCommentFileDownloadRegistry } from "./comment-file-download-registry";
import { createVisualCommentFileDescriptor, mergeVisualCommentFileContentType } from "./comment-file-preload";

export interface IVisualCommentMediaFile extends IMediaFile {
  commentId: number;
}

interface IUseVisualCommentFilesOptions {
  comments: readonly IComment[] | undefined;
  enabled: boolean;
}

const triggerBrowserDownload = (url: string, fileName: string): void => {
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const revokeObjectUrlLater = (url: string): void => {
  setTimeout(() => URL.revokeObjectURL(url), 100);
};

export const useVisualCommentFiles = ({ comments, enabled }: IUseVisualCommentFilesOptions) => {
  const { getCommentFiles } = useGetCommentFiles();
  const [commentFiles, setCommentFiles] = useState<Record<number, IVisualCommentMediaFile>>({});
  const [downloadRegistry] = useState(createCommentFileDownloadRegistry);

  useEffect(() => {
    const previewAbortController = new AbortController();
    const objectUrls = new Set<string>();
    let isCurrent = true;

    downloadRegistry.abortAll();

    if (!enabled || !comments) {
      setCommentFiles({});
      return () => {
        isCurrent = false;
        previewAbortController.abort();
        downloadRegistry.abortAll();
      };
    }

    const filesByCommentId: Record<number, IVisualCommentMediaFile> = {};
    const filesToPreload: IVisualCommentMediaFile[] = [];

    comments.forEach(comment => {
      const descriptor = createVisualCommentFileDescriptor(comment);
      if (!descriptor) {
        return;
      }

      const { shouldPreload, ...fileMetadata } = descriptor;
      const file: IVisualCommentMediaFile = {
        ...fileMetadata,
        isPreviewLoading: shouldPreload,
        isDownloading: false,
      };

      filesByCommentId[file.commentId] = file;
      if (shouldPreload) {
        filesToPreload.push(file);
      }
    });

    setCommentFiles(filesByCommentId);

    const finishPreview = (commentId: number) => {
      setCommentFiles(previousFiles => {
        const file = previousFiles[commentId];
        return file ? { ...previousFiles, [commentId]: { ...file, isPreviewLoading: false } } : previousFiles;
      });
    };

    const preloadCommentFile = async (file: IVisualCommentMediaFile) => {
      try {
        const result = await getCommentFiles(undefined, {
          commentId: file.commentId,
          signal: previewAbortController.signal,
        });

        if (!result || !isCurrent) {
          if (isCurrent) {
            finishPreview(file.commentId);
          }
          return;
        }

        const url = URL.createObjectURL(result.blob);
        objectUrls.add(url);

        setCommentFiles(previousFiles => {
          const currentFile = previousFiles[file.commentId];
          if (!currentFile) {
            return previousFiles;
          }

          return {
            ...previousFiles,
            [file.commentId]: {
              ...currentFile,
              url,
              contentType: mergeVisualCommentFileContentType(currentFile.contentType, result.blob.type),
              fileName: result.fileName || currentFile.fileName,
              isPreviewLoading: false,
            },
          };
        });
      } catch (error) {
        if (!previewAbortController.signal.aborted) {
          console.error("Error fetching comment file preview:", error);
          finishPreview(file.commentId);
        }
      }
    };

    filesToPreload.forEach(file => void preloadCommentFile(file));

    return () => {
      isCurrent = false;
      previewAbortController.abort();
      downloadRegistry.abortAll();
      objectUrls.forEach(url => URL.revokeObjectURL(url));
    };
  }, [comments, downloadRegistry, enabled]);

  const downloadCommentFile = async (file: IVisualCommentMediaFile): Promise<void> => {
    if (file.url) {
      triggerBrowserDownload(file.url, file.fileName || "file");
      return;
    }

    const downloadAbortController = downloadRegistry.start(file.commentId);
    if (!downloadAbortController) {
      return;
    }
    setCommentFiles(previousFiles => {
      const currentFile = previousFiles[file.commentId];
      return currentFile
        ? { ...previousFiles, [file.commentId]: { ...currentFile, isDownloading: true } }
        : previousFiles;
    });

    let temporaryUrl: string | null = null;

    try {
      const result = await getCommentFiles(undefined, {
        commentId: file.commentId,
        signal: downloadAbortController.signal,
      });

      if (!result || downloadAbortController.signal.aborted) {
        return;
      }

      temporaryUrl = URL.createObjectURL(result.blob);
      triggerBrowserDownload(temporaryUrl, result.fileName || file.fileName || "file");
    } catch (error) {
      if (!downloadAbortController.signal.aborted) {
        console.error("Download failed", error);
      }
    } finally {
      if (temporaryUrl) {
        revokeObjectUrlLater(temporaryUrl);
      }

      if (downloadRegistry.finish(file.commentId, downloadAbortController)) {
        setCommentFiles(previousFiles => {
          const currentFile = previousFiles[file.commentId];
          return currentFile
            ? { ...previousFiles, [file.commentId]: { ...currentFile, isDownloading: false } }
            : previousFiles;
        });
      }
    }
  };

  return { commentFiles, downloadCommentFile };
};
